import React, { useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";

interface TypingIndicatorProps {
	className?: string;
	style?: React.CSSProperties;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ className = "", style }) => {
	return (
		<div
			className={`absolute inset-0 flex items-center gap-0.5 ${className}`}
			style={style}
			aria-hidden="true"
		>
			<span
				className="inline-block w-1.5 h-1.5 rounded-full bg-gray-700 animate-typing-dot"
				style={{ animationDelay: "0ms" }}
			/>
			<span
				className="inline-block w-1.5 h-1.5 rounded-full bg-gray-700 animate-typing-dot"
				style={{ animationDelay: "200ms" }}
			/>
			<span
				className="inline-block w-1.5 h-1.5 rounded-full bg-gray-700 animate-typing-dot"
				style={{ animationDelay: "400ms" }}
			/>
		</div>
	);
};

interface ChatMessage {
	text: string;
	avatarColor?: string; // Hex color for avatar background (used when no avatarImage)
	avatarInitials?: string; // Initials to display in avatar (used when no avatarImage)
	avatarImage?: string; // Image URL or path for avatar
	side: "left" | "right";
	bubbleColor: string; // Hex color for bubble background
}

interface StruggleScenario {
	problem: string;
	messages: ChatMessage[];
	alignment: "left" | "right"; // Which side the problem statement aligns to
}

interface StruggleProps {
	title?: string;
	subtitle?: string;
	bgColor?: string;
	scenarios?: StruggleScenario[];
	conclusion?: string;
	ctaText?: string;
	ctaLink?: string;
	ctaChatText?: string;
}

const defaultScenarios: StruggleScenario[] = [
	{
		problem: "The site looked perfect in Figma, but the build missed the mark.",
		alignment: "right",
		messages: [
			{
				text: "It matches the comps...mostly.",
				avatarImage: "/images/avatar-1.png",
				side: "left",
				bubbleColor: "#F0F3FF",
			},
			{
				text: "Something feels off...",
				avatarImage: "/images/avatar-2.png",
				side: "right",
				bubbleColor: "#F1F9F8",
			},
		],
	},
	{
		problem:
			"You had to explain why a promised feature was 'impossible' (when really, they just didn't know how to build it).",
		alignment: "left",
		messages: [
			{
				text: "How do I explain this to the client?",
				avatarImage: "/images/avatar-3.png",
				side: "right",
				bubbleColor: "#F1F9F8",
			},
			{
				text: "Can we spin this?",
				avatarImage: "/images/avatar-4.png",
				side: "left",
				bubbleColor: "#F0F3FF",
			},
		],
	},
	{
		problem: "Your 'go-to' dev ghosted when the stakes were highest.",
		alignment: "right",
		messages: [
			{
				text: "Following up again...Are they online?",
				avatarImage: "/images/avatar-1.png",
				side: "left",
				bubbleColor: "#F0F3FF",
			},
			{
				text: "Still no response 😐",
				avatarImage: "/images/avatar-2.png",
				side: "right",
				bubbleColor: "#F1F9F8",
			},
		],
	},
];

const Struggle: React.FC<StruggleProps> = ({
	title = "Who do you call when the Dev Partner You Counted On Falls Through?",
	subtitle = "You know the story...",
	bgColor = "bg-white",
	scenarios = defaultScenarios,
	conclusion = "At Crafted, we aspire to be the last development partner your agency ever works with.",
	ctaText = "Let's Team Up",
	ctaLink = "/contact",
	ctaChatText = "We've got this.",
}) => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const controllerRef = useRef<any>(null);

	const TYPING_DURATION_MS = 1600;

	// Set up ScrollMagic: sequential message flow per scenario (first avatar → first typing → first text → second avatar → second typing → second text)
	useEffect(() => {
		if (!sectionRef.current) return;
		if (typeof window === "undefined" || !window.document) return;

		const timeouts: ReturnType<typeof setTimeout>[] = [];
		const clearAll = () => {
			timeouts.forEach((t) => clearTimeout(t));
			timeouts.length = 0;
		};
		let cancelled = false;

		import("scrollmagic")
			.then((ScrollMagic) => {
				if (cancelled || !sectionRef.current) return;
				const controller = new ScrollMagic.default.Controller();
				controllerRef.current = controller;

				const scenarioElements =
					sectionRef.current.querySelectorAll<HTMLElement>(".struggle-scenario");

				scenarioElements.forEach((scenarioEl) => {
					const problemEl = scenarioEl.querySelector<HTMLElement>(".struggle-problem");
					const firstRow = scenarioEl.querySelector<HTMLElement>(".struggle-msg-first");
					const secondRow = scenarioEl.querySelector<HTMLElement>(".struggle-msg-second");
					if (!problemEl || !firstRow || !secondRow) return;

					const firstAvatar = firstRow.querySelector<HTMLElement>(".struggle-avatar");
					const secondAvatar = secondRow.querySelector<HTMLElement>(".struggle-avatar");
					const firstBubble = firstRow.querySelector<HTMLElement>(".struggle-bubble");
					const secondBubble = secondRow.querySelector<HTMLElement>(".struggle-bubble");
					const firstTyping = firstRow.querySelector<HTMLElement>(".struggle-typing");
					const firstText = firstRow.querySelector<HTMLElement>(".struggle-msg-text");
					const secondTyping = secondRow.querySelector<HTMLElement>(".struggle-typing");
					const secondText = secondRow.querySelector<HTMLElement>(".struggle-msg-text");
					if (
						!firstAvatar ||
						!secondAvatar ||
						!firstBubble ||
						!secondBubble ||
						!firstTyping ||
						!firstText ||
						!secondTyping ||
						!secondText
					)
						return;

					new ScrollMagic.default.Scene({
						triggerElement: scenarioEl,
						triggerHook: 0.8,
					})
						.on("enter", () => {
							if (scenarioEl.getAttribute("data-struggle-animated") === "true") return;
							scenarioEl.setAttribute("data-struggle-animated", "true");

							// 0ms: problem fade in, first message avatar + bubble entrance
							problemEl.style.transition = "opacity 0.6s ease-out";
							problemEl.style.opacity = "1";
							firstAvatar.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
							firstAvatar.style.opacity = "1";
							firstAvatar.style.transform = "translateY(0)";
							firstBubble.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
							firstBubble.style.opacity = "1";
							firstBubble.style.transform = "translateY(0)";
							// First typing already visible; first text stays hidden.

							// After TYPING_DURATION: hide first typing, show first text; second avatar + bubble entrance; show second typing
							timeouts.push(
								setTimeout(() => {
									firstTyping.style.transition = "opacity 0.2s ease-out";
									firstTyping.style.visibility = "hidden";
									firstTyping.style.opacity = "0";
									firstText.style.transition = "opacity 0.2s ease-out";
									firstText.style.visibility = "visible";
									firstText.style.opacity = "1";

									secondAvatar.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
									secondAvatar.style.opacity = "1";
									secondAvatar.style.transform = "translateY(0)";
									secondBubble.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
									secondBubble.style.opacity = "1";
									secondBubble.style.transform = "translateY(0)";

									secondTyping.style.transition = "opacity 0.2s ease-out";
									secondTyping.style.visibility = "visible";
									secondTyping.style.opacity = "1";
								}, TYPING_DURATION_MS),
							);

							// After 2 * TYPING_DURATION: hide second typing, show second text
							timeouts.push(
								setTimeout(() => {
									secondTyping.style.transition = "opacity 0.2s ease-out";
									secondTyping.style.visibility = "hidden";
									secondTyping.style.opacity = "0";
									secondText.style.transition = "opacity 0.2s ease-out";
									secondText.style.visibility = "visible";
									secondText.style.opacity = "1";
								}, TYPING_DURATION_MS * 2),
							);
						})
						.addTo(controller);
				});

				// CTA chat bubble + avatar: same entrance animation + typing → text when footer CTA row enters viewport
				const ctaChatRow = sectionRef.current.querySelector<HTMLElement>(".struggle-cta-chat");
				const ctaBubble = sectionRef.current.querySelector<HTMLElement>(".struggle-cta-bubble");
				const ctaAvatar = sectionRef.current.querySelector<HTMLElement>(".struggle-cta-avatar");
				const ctaTyping = sectionRef.current.querySelector<HTMLElement>(".struggle-cta-typing");
				const ctaText = sectionRef.current.querySelector<HTMLElement>(".struggle-cta-msg-text");
				if (ctaChatRow && ctaBubble && ctaAvatar && ctaTyping && ctaText) {
					new ScrollMagic.default.Scene({
						triggerElement: ctaChatRow,
						triggerHook: 0.8,
					})
						.on("enter", () => {
							if (ctaChatRow.getAttribute("data-struggle-cta-animated") === "true") return;
							ctaChatRow.setAttribute("data-struggle-cta-animated", "true");
							ctaBubble.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
							ctaBubble.style.opacity = "1";
							ctaBubble.style.transform = "translateY(0)";
							ctaAvatar.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
							ctaAvatar.style.opacity = "1";
							ctaAvatar.style.transform = "translateY(0)";
							// After TYPING_DURATION: hide typing, show CTA text
							timeouts.push(
								setTimeout(() => {
									ctaTyping.style.transition = "opacity 0.2s ease-out";
									ctaTyping.style.visibility = "hidden";
									ctaTyping.style.opacity = "0";
									ctaText.style.transition = "opacity 0.2s ease-out";
									ctaText.style.visibility = "visible";
									ctaText.style.opacity = "1";
								}, TYPING_DURATION_MS),
							);
						})
						.addTo(controller);
				}
			})
			.catch((err) => {
				console.error("Failed to load ScrollMagic:", err);
			});

		return () => {
			cancelled = true;
			clearAll();
			if (controllerRef.current) {
				controllerRef.current.destroy(true);
				controllerRef.current = null;
			}
		};
	}, [scenarios]);

	return (
		<div className={`${bgColor} py-[clamp(40px,6.25vw,80px)] px-2 xl:px-0`}>
			<div
				ref={sectionRef}
				className="container mx-auto max-w-6xl px-4"
			>
				{/* Header */}
				<div className="mb-16">
					{title && <h2 className="font-bold leading-tight mb-4">{title}</h2>}
					{subtitle && <p className="text-[clamp(18px,1.67vw,24px)]">{subtitle}</p>}
				</div>

				{/* Scenarios */}
				<div className="mb-20">
					{scenarios.map((scenario, scenarioIndex) => (
						<div
							key={scenarioIndex}
							className={`struggle-scenario relative w-[90%] md:w-4/6 my-[clamp(100px,20.83vw,300px)] ${
								scenario.alignment === "left" ? "text-left mr-auto" : "text-right ml-auto"
							}`}
						>
							{/* Problem Statement */}
							<div
								className="struggle-problem mb-8"
								style={{ opacity: 0 }}
							>
								<p className="font-bold text-[clamp(20px,2.5vw,36px)] leading-tight">
									{scenario.problem}
								</p>
							</div>

							{/* Chat Bubbles */}
							<div className="relative flex flex-col gap-10">
								{scenario.messages.map((message, messageIndex) => {
									const isLeft = message.side === "left";
									const isFirst = messageIndex === 0;
									// First message: show typing first, hide text. Second: hide both until timeline.
									const typingVisible = isFirst;
									const textVisible = false;
									return (
										<div
											key={messageIndex}
											className={`struggle-msg-${
												isFirst ? "first" : "second"
											} flex items-end gap-3 ${isLeft ? "justify-start" : "justify-end"}`}
										>
											{isLeft && (
												<div
													className={`struggle-avatar relative flex-shrink-0 w-10 md:w-14 h-10 md:h-14 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
														message.avatarImage ? "overflow-hidden" : ""
													}`}
													style={{
														...(message.avatarImage
															? {}
															: { backgroundColor: message.avatarColor || "#9DADFC" }),
														opacity: 0,
														transform: "translateY(15px)",
													}}
												>
													{message.avatarImage ? (
														<Image
															src={message.avatarImage}
															alt=""
															layout="fill"
															className="object-cover"
															sizes="(max-width: 768px) 40px, 56px"
														/>
													) : (
														message.avatarInitials || "D"
													)}
												</div>
											)}
											<div
												className="struggle-bubble relative rounded-2xl py-[clamp(10px,1.39vw,20px)] px-[clamp(16px,1.94vw,28px)] min-h-[44px] flex items-center shadow-md"
												style={{
													backgroundColor: message.bubbleColor,
													borderTopLeftRadius: isLeft ? "4px" : "16px",
													borderTopRightRadius: isLeft ? "16px" : "4px",
													opacity: 0,
													transform: "translateY(15px)",
												}}
											>
												<TypingIndicator
													className="struggle-typing py-[clamp(10px,1.39vw,20px)] px-[clamp(16px,1.94vw,28px)]"
													style={{
														visibility: typingVisible ? "visible" : "hidden",
														opacity: typingVisible ? 1 : 0,
													}}
												/>
												<div
													className="struggle-msg-text text-[clamp(18px,1.67vw,24px)]"
													style={{
														visibility: textVisible ? "visible" : "hidden",
														opacity: textVisible ? 1 : 0,
													}}
												>
													{message.text}
												</div>
											</div>
											{!isLeft && (
												<div
													className={`struggle-avatar relative flex-shrink-0 w-10 md:w-14 h-10 md:h-14 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
														message.avatarImage ? "overflow-hidden" : ""
													}`}
													style={{
														...(message.avatarImage
															? {}
															: { backgroundColor: message.avatarColor || "#A8D7D3" }),
														opacity: 0,
														transform: "translateY(15px)",
													}}
												>
													{message.avatarImage ? (
														<Image
															src={message.avatarImage}
															alt=""
															layout="fill"
															className="object-cover"
															sizes="(max-width: 768px) 40px, 56px"
														/>
													) : (
														message.avatarInitials || "A"
													)}
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>

				{/* Footer - Conclusion and CTA */}
				<div className="flex flex-col gap-6">
					<div className="flex-1 w-3/4">
						{conclusion && (
							<p className="font-bold text-[clamp(24px,3.33vw,48px)] leading-none mb-6 md:mb-0">
								{conclusion}
							</p>
						)}
					</div>
					<div className="flex flex-col md:flex-row items-start md:items-center gap-4">
						<Link href={ctaLink}>
							<a className="bg-blue text-white font-display py-4 px-8 rounded-lg hover:bg-blue/80 transition-colors shadow-lg text-lg whitespace-nowrap">
								{ctaText}
							</a>
						</Link>
					</div>
					{/* CTA Chat Bubble */}
					<div className="struggle-cta-chat flex items-center justify-end gap-2">
						<div
							className="struggle-cta-bubble relative rounded-2xl rounded-br-sm py-[clamp(10px,1.39vw,20px)] px-[clamp(16px,1.94vw,28px)] bg-blue/10 shadow-md min-h-[44px] flex items-center"
							style={{ opacity: 0, transform: "translateY(15px)" }}
						>
							<TypingIndicator className="struggle-cta-typing px-[clamp(16px,1.94vw,28px)] py-[clamp(10px,1.39vw,20px)]" />
							<div
								className="struggle-cta-msg-text text-[clamp(18px,1.67vw,24px)]"
								style={{ visibility: "hidden", opacity: 0 }}
							>
								{ctaChatText}
							</div>
						</div>
						<div
							className="struggle-cta-avatar relative flex-shrink-0 w-10 md:w-14 h-10 md:h-14 rounded-full bg-black flex items-center justify-center"
							style={{ opacity: 0, transform: "translateY(15px)" }}
						>
							<Image
								src="/images/crafted-avatar.png"
								alt="Crafted Logo"
								layout="fill"
								className="object-cover"
								sizes="(max-width: 768px) 40px, 56px"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Struggle;
